import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Upload, Mic2, Music, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const UploadPage = () => {
    const { token, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        tags: '',
        performance_type: 'freestyle',
        duration_seconds: 180
    });
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStep, setUploadStep] = useState('idle'); // idle, uploading, confirming, success

    const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        // Accept common audio formats — m4a is what phones record natively.
        const okExt = /\.(mp3|m4a|aac|wav|ogg|oga|flac|mp4)$/i.test(selectedFile.name);
        const okMime = (selectedFile.type || '').startsWith('audio/');
        if (!okExt && !okMime) {
            toast.error('Please upload an audio file (MP3, M4A, WAV, OGG, FLAC).');
            return;
        }

        // Validate file size (50MB max)
        if (selectedFile.size > 50 * 1024 * 1024) {
            toast.error('File too large. Maximum size is 50MB.');
            return;
        }

        setFile(selectedFile);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!isAuthenticated) {
            toast.error('Please login to upload');
            navigate('/login');
            return;
        }

        if (!formData.title.trim()) {
            toast.error('Please enter a title');
            return;
        }

        setUploading(true);
        setUploadStep('uploading');
        setUploadProgress(0);

        try {
            const tags = formData.tags
                .split(',')
                .map(t => t.trim().toLowerCase())
                .filter(t => t.length > 0);

            // If we have a file and R2 is configured, upload it
            if (file) {
                // Step 1: Get upload URL
                const urlResponse = await axios.post(
                    `${API}/performances/upload-url`,
                    {
                        title: formData.title,
                        description: formData.description,
                        tags,
                        performance_type: formData.performance_type,
                        duration_seconds: formData.duration_seconds
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                const { performance_id, upload_url } = urlResponse.data;

                if (upload_url) {
                    // R2 configured — upload directly to presigned URL
                    setUploadProgress(10);
                    await axios.put(upload_url, file, {
                        headers: { 'Content-Type': file.type || 'audio/mpeg' },
                        onUploadProgress: (progressEvent) => {
                            const progress = Math.round(
                                10 + (progressEvent.loaded / (progressEvent.total || 1)) * 70
                            );
                            setUploadProgress(progress);
                        }
                    });
                    setUploadStep('confirming');
                    setUploadProgress(90);
                    await axios.post(
                        `${API}/performances/${performance_id}/confirm`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                } else {
                    // Send raw audio bytes to the Worker, which stores them in R2.
                    setUploadProgress(10);
                    await axios.post(
                        `${API}/performances/${performance_id}/upload-audio`,
                        file,
                        {
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type || 'audio/mpeg' },
                            onUploadProgress: (progressEvent) => {
                                const progress = Math.round(
                                    10 + (progressEvent.loaded / (progressEvent.total || 1)) * 85
                                );
                                setUploadProgress(progress);
                            }
                        }
                    );
                }

                setUploadProgress(100);
                setUploadStep('success');
                toast.success('Performance uploaded successfully!');
                setTimeout(() => navigate(`/performance/${performance_id}`), 1500);

            } else {
                // Create mock performance (no file)
                const response = await axios.post(
                    `${API}/performances/create-mock`,
                    {
                        title: formData.title,
                        description: formData.description,
                        tags,
                        performance_type: formData.performance_type,
                        duration_seconds: formData.duration_seconds
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                setUploadProgress(100);
                setUploadStep('success');
                toast.success('Performance created successfully!');
                setTimeout(() => navigate(`/performance/${response.data.performance_id}`), 1500);
            }

        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.response?.data?.detail || 'Upload failed');
            setUploadStep('idle');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="font-display font-bold text-2xl sm:text-3xl mb-2">
                    DROP YOUR <span className="text-neon-purple">BARS</span>
                </h1>
                <p className="text-zinc-400 text-sm">
                    Upload your performance and let the community judge your skill
                </p>
            </div>

            <div className="glass-panel rounded-sm p-6" data-testid="upload-form">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Audio Upload */}
                    <div>
                        <Label className="text-sm text-zinc-400 mb-2 block">Audio File (Optional)</Label>
                        <div 
                            className={`border-2 border-dashed rounded-sm p-8 text-center transition-colors ${
                                file ? 'border-primary/50 bg-primary/5' : 'border-white/10 hover:border-white/20'
                            }`}
                        >
                            <input
                                type="file"
                                accept="audio/*,.mp3,.m4a,.aac,.wav,.ogg,.oga,.flac"
                                onChange={handleFileChange}
                                className="hidden"
                                id="audio-upload"
                                data-testid="audio-file-input"
                            />
                            <label htmlFor="audio-upload" className="cursor-pointer">
                                {file ? (
                                    <div className="flex items-center justify-center gap-3">
                                        <Music className="w-8 h-8 text-primary" />
                                        <div className="text-left">
                                            <p className="font-medium text-white">{file.name}</p>
                                            <p className="text-sm text-zinc-400">
                                                {(file.size / 1024 / 1024).toFixed(2)} MB
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <Upload className="w-10 h-10 mx-auto text-zinc-500 mb-3" />
                                        <p className="text-zinc-400 mb-1">Drag & drop or click to upload</p>
                                        <p className="text-xs text-zinc-500">MP3, M4A, WAV, OGG up to 50MB • Max 5 minutes</p>
                                    </>
                                )}
                            </label>
                        </div>
                        <p className="text-xs text-zinc-500 mt-2 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Add an audio file, or leave it empty to create a text-only entry.
                        </p>
                    </div>

                    {/* Title */}
                    <div>
                        <Label htmlFor="title" className="text-sm text-zinc-400">Title *</Label>
                        <Input
                            id="title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            placeholder="Fire in the Booth"
                            required
                            className="bg-black/50 border-white/10 focus:border-primary/50 mt-1 rounded-sm"
                            data-testid="upload-title"
                        />
                    </div>

                    {/* Freestyle toggle */}
                    <div className="flex items-center gap-3 py-1">
                        <Checkbox
                            id="freestyle"
                            checked={formData.performance_type === 'freestyle'}
                            onCheckedChange={(checked) =>
                                setFormData({ ...formData, performance_type: checked ? 'freestyle' : 'verse' })
                            }
                            data-testid="upload-freestyle"
                        />
                        <Label htmlFor="freestyle" className="text-sm text-zinc-300 cursor-pointer select-none">
                            This is a freestyle
                        </Label>
                    </div>

                    {/* Description */}
                    <div>
                        <Label htmlFor="description" className="text-sm text-zinc-400">Description (Optional)</Label>
                        <Textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="What's the story behind this performance?"
                            rows={3}
                            className="bg-black/50 border-white/10 focus:border-primary/50 mt-1 rounded-sm resize-none"
                            data-testid="upload-description"
                        />
                    </div>

                    {/* Tags */}
                    <div>
                        <Label htmlFor="tags" className="text-sm text-zinc-400">Tags (comma separated)</Label>
                        <Input
                            id="tags"
                            value={formData.tags}
                            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                            placeholder="trap, lyrical, storytelling"
                            className="bg-black/50 border-white/10 focus:border-primary/50 mt-1 rounded-sm font-mono text-sm"
                            data-testid="upload-tags"
                        />
                    </div>

                    {/* Upload Progress */}
                    {uploading && (
                        <div className="space-y-2">
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-primary transition-all duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-zinc-400">
                                    {uploadStep === 'uploading' && 'Uploading audio...'}
                                    {uploadStep === 'confirming' && 'Processing...'}
                                    {uploadStep === 'success' && 'Complete!'}
                                </span>
                                <span className="font-mono text-primary">{uploadProgress}%</span>
                            </div>
                        </div>
                    )}

                    {/* Submit */}
                    <Button
                        type="submit"
                        disabled={uploading || !formData.title.trim()}
                        className="w-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest rounded-sm shadow-glow-purple"
                        data-testid="upload-submit"
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {uploadStep === 'success' ? 'Done!' : 'Uploading...'}
                            </>
                        ) : (
                            <>
                                <Mic2 className="w-4 h-4 mr-2" />
                                Drop Performance
                            </>
                        )}
                    </Button>
                </form>
            </div>

            {/* Guidelines */}
            <div className="mt-8 glass-panel rounded-sm p-6">
                <h3 className="font-display font-bold mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-primary" />
                    Guidelines
                </h3>
                <ul className="space-y-2 text-sm text-zinc-400">
                    <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#7FFF00] mt-0.5 flex-shrink-0" />
                        Keep performances under 5 minutes
                    </li>
                    <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#7FFF00] mt-0.5 flex-shrink-0" />
                        Original content only - no copyrighted beats without permission
                    </li>
                    <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#7FFF00] mt-0.5 flex-shrink-0" />
                        Good audio quality helps judges evaluate your skills
                    </li>
                    <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#7FFF00] mt-0.5 flex-shrink-0" />
                        Add relevant tags to help others discover your work
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default UploadPage;
